import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { supabase } from '../../lib/supabase';
import { 
  fetchTranslationKeys, 
  fetchTranslationStrings, 
  updateTranslationKey,
  upsertTranslationString,
  createTranslationKey,
  deleteTranslationKey,
  checkKeyExists,
  TranslationKey, 
  TranslationString 
} from '../../services/translationService';
import { aiTranslationService } from '../../services/translationAI';
import EmptyView from '../shared/EmptyView';
import EmptyStateEnhanced from '../../components/EmptyStateEnhanced';
import Button from '../../components/Button/Button';
import ProjectLanguageSelector from '../../components/ProjectLanguageSelector/ProjectLanguageSelector';
import EditableTable from '../../components/EditableTable/EditableTable';
import TranslationKeyDialog from '../../components/TranslationKeyDialog/TranslationKeyDialog';
import EditKeyDialog from '../../components/EditKeyDialog/EditKeyDialog';
import SelectionActionBar from '../../components/SelectionActionBar';
import ReviewDialog from '../../components/ReviewDialog/ReviewDialog';
import SelectReviewDialog from '../../components/SelectReviewDialog/SelectReviewDialog';
import ImportTranslationsDialog from '../../components/ImportTranslationsDialog/ImportTranslationsDialog';
import ExportTranslationsDialog from '../../components/ExportTranslationsDialog';
import ContentCard from '../../components/ContentCard/ContentCard';
// @ts-ignore
import searchIcon from '../../image-assets/icon.search.svg';
// @ts-ignore
import addIcon from '../../image-assets/icon.add.svg';
// @ts-ignore
import importIcon from '../../image-assets/icon.import.svg';
// @ts-ignore
import dropdownIcon from '../../image-assets/icon.arrows.dropdown.svg';
// @ts-ignore
import spinnerIcon from '../../image-assets/icon.spinner.svg';
// @ts-ignore
import keyIcon from '../../image-assets/icon.key.svg';
import languagesData from '../../data/languages.json';
import '../SharedStyles.css';
import './TranslationsView.css';
import { useLanguages } from '../../context/LanguageContext';
import { setContentWithHighlightedPlaceholders } from '../../utils/placeholderUtils';

interface TranslationData {
  key: TranslationKey;
  strings: TranslationString[];
}

interface GroupedTranslations {
  [prefix: string]: TranslationData[];
}

const TranslationsView: React.FC = () => {
  const { selectedProject, selectedTeam, projects } = useWorkspace();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [translationData, setTranslationData] = useState<TranslationData[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isKeyDialogOpen, setIsKeyDialogOpen] = useState(false);
  const [isCreatingKey, setIsCreatingKey] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<TranslationKey | null>(null);
  const [isDeletingKey, setIsDeletingKey] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [keyToEdit, setKeyToEdit] = useState<TranslationKey | null>(null);
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [isSelectReviewDialogOpen, setIsSelectReviewDialogOpen] = useState(false);
  const [isAddingToReview, setIsAddingToReview] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const { 
    selectedLanguage,
    firstLanguage,
    getLanguageName,
    setSelectedLanguage: handleLanguageSelect,
    availableLanguages
  } = useLanguages();

  // State for action bar
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [focusedCellInfo, setFocusedCellInfo] = useState<{
    rowKey: string;
    columnKey: string;
    content: string;
    sourceLanguage: string | null;
    sourceText: string;
  } | null>(null);

  // Ensure a language is selected on load
  useEffect(() => {
    // Only attempt to select if we have at least two languages
    if (availableLanguages.length >= 2) {
      const firstLang = availableLanguages[0];
      const secondLang = availableLanguages[1];

      // If no language is selected OR the selected one is the FIRST default language,
      // select the second language instead.
      if (!selectedLanguage || selectedLanguage === firstLang) {
        handleLanguageSelect(secondLang);
      }
    }
  }, [selectedLanguage, availableLanguages, handleLanguageSelect]);

  const loadTranslations = async () => {
    if (!selectedProject) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch all keys
      const keys = await fetchTranslationKeys(selectedProject.id);
      
      // Fetch strings for each key
      const translationsWithStrings = await Promise.all(
        keys.map(async (key) => {
          const strings = await fetchTranslationStrings(key.id);
          return { key, strings };
        })
      );
      
      setTranslationData(translationsWithStrings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTranslations();
  }, [selectedProject]);

  // Listen for search query updates from the toolbar
  useEffect(() => {
    const handleSearchUpdate = (event: CustomEvent<{ searchQuery: string }>) => {
      setSearchQuery(event.detail.searchQuery);
    };

    window.addEventListener('update-search-query', handleSearchUpdate as EventListener);
    return () => {
      window.removeEventListener('update-search-query', handleSearchUpdate as EventListener);
    };
  }, []);

  // Listen for new key dialog open event from toolbar
  useEffect(() => {
    const handleOpenKeyDialog = () => {
      setIsKeyDialogOpen(true);
    };

    window.addEventListener('open-key-dialog', handleOpenKeyDialog);
    return () => {
      window.removeEventListener('open-key-dialog', handleOpenKeyDialog);
    };
  }, []);

  // Listen for import dialog open event from toolbar
  useEffect(() => {
    const handleOpenImportDialog = () => {
      setIsImportDialogOpen(true);
    };

    window.addEventListener('open-import-dialog', handleOpenImportDialog);
    return () => {
      window.removeEventListener('open-import-dialog', handleOpenImportDialog);
    };
  }, []);

  // Listen for export dialog open event from toolbar
  useEffect(() => {
    const handleOpenExportDialog = () => {
      setIsExportDialogOpen(true);
    };

    window.addEventListener('open-export-dialog', handleOpenExportDialog);
    return () => {
      window.removeEventListener('open-export-dialog', handleOpenExportDialog);
    };
  }, []);

  // Filter translations based on search query
  const filteredTranslations = translationData.filter(data => {
    const searchLower = searchQuery.toLowerCase();
    
    // Search in key
    if (data.key.key.toLowerCase().includes(searchLower)) {
      return true;
    }
    
    // Search in translation strings
    return data.strings.some(str => 
      str.value.toLowerCase().includes(searchLower)
    );
  });

  // Get columns for a prefix group
  const getColumnsForPrefix = (prefix: string) => {
    const prefixKeys = groupedTranslations[prefix]?.map(data => data.key.id) || [];
    const allPrefixKeysSelected = prefixKeys.length > 0 && prefixKeys.every(id => selectedRows.has(id));
    
    const columns = [
      { 
        header: prefix,
        key: 'key',
        editable: true,
        isSelectable: true,
        isSelected: allPrefixKeysSelected,
        onHeaderSelect: () => {
          setSelectedRows(prev => {
            const newSet = new Set(prev);
            prefixKeys.forEach(id => {
              if (allPrefixKeysSelected) {
                newSet.delete(id);
              } else {
                newSet.add(id);
              }
            });
            return newSet;
          });
        }
      }
    ];

    // Only add one language column if both languages are the same
    if (firstLanguage === selectedLanguage) {
      columns.push(
        { 
          header: getLanguageName(firstLanguage), 
          key: firstLanguage || 'first', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        },
        { 
          header: '', 
          key: 'blank', 
          editable: false,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        }
      );
    } else {
      columns.push(
        { 
          header: getLanguageName(firstLanguage), 
          key: firstLanguage || 'first', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        },
        { 
          header: getLanguageName(selectedLanguage), 
          key: selectedLanguage || 'second', 
          editable: true,
          isSelectable: false,
          isSelected: false,
          onHeaderSelect: () => {} 
        }
      );
    }

    return columns;
  };

  // Get cell content based on column
  const getCellContent = (row: TranslationData, columnKey: string) => {
    if (columnKey === 'key') {
      return row.key.key;
    }
    if (columnKey === 'blank') {
      return '';
    }
    return row.strings.find(s => s.language_code === columnKey)?.value || '';
  };

  // Group translations by prefix
  const groupedTranslations = useMemo(() => {
    return filteredTranslations.reduce((groups: GroupedTranslations, item) => {
      // Extract prefix (everything up to the first non-alphanumeric character)
      const prefix = item.key.key.match(/^[a-zA-Z0-9]+/)?.[0] || 'Other';
      
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(item);
      
      return groups;
    }, {});
  }, [filteredTranslations]);

  // Sort prefixes alphabetically
  const sortedPrefixes = useMemo(() => {
    return Object.keys(groupedTranslations).sort((a, b) => a.localeCompare(b));
  }, [groupedTranslations]);

  const handleRowSelect = (rowKey: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowKey);
      } else {
        newSet.delete(rowKey);
      }
      return newSet;
    });
  };

  const handleCreateReview = () => {
    setIsReviewDialogOpen(true);
  };

  const handleSaveReview = async (title: string, description: string) => {
    if (!selectedProject?.id || !user?.id) {
      return;
    }

    try {
      setIsCreatingReview(true);
      
      // Create the review with key IDs
      const { data: review, error: reviewError } = await supabase
        .from('Reviews')
        .insert([{
          title,
          description,
          project_id: selectedProject.id,
          owner_user_id: user.id,
          status: 'pending',
          created_at: new Date().toISOString(),
          last_updated_at: new Date().toISOString(),
          keys: Array.from(selectedRows) // Store the array of key IDs directly
        }])
        .select()
        .single();

      if (reviewError) {
        throw reviewError;
      }

      setIsReviewDialogOpen(false);
      setSelectedRows(new Set());
    } catch (err) {
      // console.error('Error creating review:', err);
    } finally {
      setIsCreatingReview(false);
    }
  };

  // Get selected key names
  const selectedKeyNames = useMemo(() => {
    return Array.from(selectedRows).map(keyId => {
      const translation = translationData.find((data: TranslationData) => data.key.id === keyId);
      return translation?.key.key || keyId;
    });
  }, [selectedRows, translationData]);

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  // Handle cell change
  const handleCellChange = async (rowKey: string, columnKey: string, value: string) => {
    try {
      // Find the existing row
      const existingRowIndex = translationData.findIndex(item => getRowKey(item) === rowKey);
      if (existingRowIndex === -1) {
        return;
      }

      const existingRow = translationData[existingRowIndex];
      
      // For language columns, find or create the translation string
      const existingString = existingRow.strings.find(s => 
        s.language_code === columnKey
      );
      
      // Only proceed if there's an actual change in content
      if (existingString && existingString.value === value) {
        return;
      }
      
      // Always update the database
      await upsertTranslationString(rowKey, columnKey, value, selectedProject?.id || '');
      
      // Show a simple notification that the text was updated
      showNotification('Saved');
      
      if (existingString) {
        // Update existing translation string in the model
        const updatedString = {
          ...existingString,
          value: value
        };
        
        // Update local state
        const updatedStrings = [...existingRow.strings];
        const existingStringIndex = existingRow.strings.indexOf(existingString);
        updatedStrings[existingStringIndex] = updatedString;
        
        const updatedData = [...translationData];
        updatedData[existingRowIndex] = {
          ...existingRow,
          strings: updatedStrings
        };
        
        setTranslationData(updatedData);
      } else {
        // Create new translation string
        const newString = {
          id: '',
          value: value,
          language_code: columnKey,
          key_id: existingRow.key.id,
          created_at: new Date().toISOString()
        };
        
        // Update local state
        const updatedStrings = [...existingRow.strings, newString as TranslationString];
        
        const updatedData = [...translationData];
        updatedData[existingRowIndex] = {
          ...existingRow,
          strings: updatedStrings
        };
        
        setTranslationData(updatedData);
      }
    } catch (error) {
      // console.error('Error updating cell:', error);
      showNotification('Failed to update translation');
    }
  };

  // Handle cell blur
  const handleCellBlur = () => {
    // Don't blur if we're in the process of translating
    if (window.sessionStorage.getItem('texta_translating')) {
      return;
    }
    
    // Use a small delay to allow action bar interactions
    setTimeout(() => {
      // Double-check we're not translating before blurring
      if (!window.sessionStorage.getItem('texta_translating')) {
        setIsInputFocused(false);
        setFocusedCellInfo(null);
      }
    }, 200);
  };

  // Handle creating a new translation key
  const handleCreateKey = async (keyName: string, defaultValue: string) => {
    if (!selectedProject || !firstLanguage) return;
    
    try {
      setIsCreatingKey(true);
      
      // Check if key already exists
      const keyExists = await checkKeyExists(selectedProject.id, keyName);
      if (keyExists) {
        throw new Error('A key with this name already exists in this project');
      }
      
      // Create the key
      const newKey = await createTranslationKey(selectedProject.id, keyName);
      
      // If there's a default value, create the translation string
      if (defaultValue && firstLanguage) {
        await upsertTranslationString(newKey.id, firstLanguage, defaultValue, selectedProject.id);
      }
      
      // Create the new translation data entry
      const newTranslationData = {
        key: newKey,
        strings: defaultValue ? [{
          id: 'temp',
          value: defaultValue,
          language_code: firstLanguage,
          key_id: newKey.id,
          created_at: new Date().toISOString()
        }] : []
      };

      // Add the new key to the state, maintaining alphabetical order
      setTranslationData(prev => {
        const updated = [...prev, newTranslationData];
        return updated.sort((a, b) => a.key.key.localeCompare(b.key.key));
      });
      
      showNotification('Key added');
      
      // Close the dialog
      setIsKeyDialogOpen(false);
    } catch (err) {
      // console.error('Error creating translation key:', err);
      showNotification('Failed to add key');
      // Re-throw the error to be handled by the dialog
      throw err;
    } finally {
      setIsCreatingKey(false);
    }
  };

  // Handle edit button click
  const handleEditClick = (keyId: string) => {
    const keyData = translationData.find(data => data.key.id === keyId);
    if (keyData) {
      setKeyToEdit(keyData.key);
      setIsEditDialogOpen(true);
    }
  };

  // Handle saving edited key name
  const handleSaveKeyEdit = async (newKeyName: string) => {
    if (!keyToEdit) return;
    
    try {
      setIsEditingKey(true);
      
      // Update the key name
      const updatedKey = await updateTranslationKey(keyToEdit.id, newKeyName);
      
      // Update the state with the new key name
      setTranslationData(prev => prev.map(data => 
        data.key.id === keyToEdit.id ? { ...data, key: updatedKey } : data
      ));
    } catch (err) {
      // console.error('Error updating key name:', err);
      throw err;
    } finally {
      setIsEditingKey(false);
    }
  };

  // Handle deleting a key
  const handleDeleteKey = async () => {
    if (!keyToEdit) return;
    
    try {
      // Delete the key
      await deleteTranslationKey(keyToEdit.id);
      
      // Update the state to remove the deleted key
      setTranslationData(prev => prev.filter(data => data.key.id !== keyToEdit.id));
      
      showNotification('Key deleted');
    } catch (err) {
      // console.error('Error deleting translation key:', err);
      showNotification('Failed to delete key');
      throw err;
    }
  };

  const handleAddToReview = () => {
    setIsSelectReviewDialogOpen(true);
  };

  const handleReviewSelect = async (reviewId: string) => {
    if (!selectedProject?.id) return;
    
    try {
      setIsAddingToReview(true);
      
      // Get the current review
      const { data: review, error: reviewError } = await supabase
        .from('Reviews')
        .select('keys')
        .eq('id', reviewId)
        .single();

      if (reviewError) throw reviewError;

      // Get current keys array or initialize empty array
      const currentKeys = Array.isArray(review?.keys) ? review.keys : [];
      
      // Add new keys, avoiding duplicates
      const newKeys = Array.from(selectedRows);
      const uniqueKeys = Array.from(new Set([...currentKeys, ...newKeys]));

      // Update the review with the new keys
      const { error: updateError } = await supabase
        .from('Reviews')
        .update({ keys: uniqueKeys })
        .eq('id', reviewId);

      if (updateError) throw updateError;

      // Clear selection
      setSelectedRows(new Set());
    } catch (err) {
      // console.error('Error adding keys to review:', err);
      throw err;
    } finally {
      setIsAddingToReview(false);
    }
  };

  const handleOpenKeyDialog = () => {
    setIsKeyDialogOpen(true);
  };

  const handleOpenImportDialog = () => {
    setIsImportDialogOpen(true);
  };

  const handleOpenExportDialog = () => {
    setIsExportDialogOpen(true);
  };

  const handleImportComplete = (stats: {
    addedLanguages: string[];
    addedKeys: number;
    addedStrings: number;
    skippedKeys: number;
    skippedStrings: number;
    errors: string[];
  }) => {
    // Show notification with import results
    const successMessage = `
      Import complete:
      ${stats.addedKeys} keys added
      ${stats.addedStrings} translations added
      ${stats.addedLanguages.length > 0 ? `${stats.addedLanguages.length} languages added` : ''}
    `;
    
    showNotification(stats.errors.length > 0 
      ? `Import completed with ${stats.errors.length} errors. ${successMessage}`
      : successMessage
    );
    
    // Refresh the translations
    loadTranslations();
  };

  // Function to get row key
  const getRowKey = (row: TranslationData) => row.key.id;

  // Handle cell focus
  const handleCellFocus = (rowKey: string, columnKey: string, content: string, sourceLanguage: string | null) => {
    let sourceText = '';
    
    // Find the source text for translation
    if (sourceLanguage) {
      const allTranslations = Object.values(groupedTranslations).flat();
      const rowData = allTranslations.find(item => getRowKey(item) === rowKey);
      
      if (rowData) {
        // Get content from the first language (source language), not the key
        // First try to get the source text from the first language
        if (firstLanguage) {
          sourceText = getCellContent(rowData, firstLanguage);
        }
        
        // If no translation in first language, use the key as fallback
        if (!sourceText && sourceLanguage === 'key') {
          sourceText = getCellContent(rowData, 'key');
        }
      }
    }
    
    setFocusedCellInfo({
      rowKey,
      columnKey,
      content,
      sourceLanguage,
      sourceText
    });
    
    setIsInputFocused(true);
  };

  // Handle translate
  const handleTableTranslate = async (rowKey: string, columnKey: string, translatedText: string) => {
    if (!translatedText || !translatedText.trim()) {
      return;
    }
    
    // Find the row data
    const allTranslations = Object.values(groupedTranslations).flat();
    const rowData = allTranslations.find(item => getRowKey(item) === rowKey);
    
    if (rowData) {
      // Find and update the cell in the DOM - use simplified cell key (just rowKey)
      const cellKey = rowKey;
      
      // Try different selectors to find the cell
      let cellElement: Element | null = null;
      
      // Use safer selectors that won't throw errors
      const safeSelectors = [
        `.editable-table-cell[data-row="${rowKey}"][data-col="${columnKey}"]`,
        `[data-cell-key="${cellKey}"]`,
        `#${cellKey}`,
        `.editable-table-cell[contenteditable="true"]`
      ];
      
      // Try each selector
      for (const selector of safeSelectors) {
        try {
          const element = document.querySelector(selector);
          if (element) {
            cellElement = element;
            break;
          }
        } catch (err) {
        }
      }
      
      if (!cellElement) {
        // Try a broader search as a fallback
        const allCells = document.querySelectorAll('.editable-table-cell');
        
        allCells.forEach(cell => {
          if (cell.textContent && cell.textContent.trim() === '') {
            cellElement = cell;
          }
        });
      }
      
      if (cellElement) {
        // Add translating class to show the magic border
        cellElement.classList.add('translating');
        
        // Set initial empty content before animation
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.textContent = '';
        }
        
        // Create a unique animation effect using inline style with more pronounced animation
        const animateWords = (text: string) => {
          // Split by spaces but preserve spaces in the output
          const words = text.split(/\s+/);
          return words.map((word: string, index: number) => {
            // Calculate staggered delay based on word position
            const delay = 0.08 * index;
            // Make animation more dramatic for longer words
            const distance = Math.min(10 + word.length, 20);
            
            return `<span 
              class="word-animate" 
              style="
                animation-delay: ${delay}s; 
                animation-duration: 0.6s;
                display: inline-block; 
                opacity: 0; 
                transform: translateY(${distance}px);
                animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay}s forwards;
                position: relative;
              "
            >${word}</span>`;
          }).join(' ');
        };
        
        const animatedHTML = animateWords(translatedText);
        
        // Apply animated HTML immediately
        if (cellElement.getAttribute('contenteditable') === 'true') {
          cellElement.innerHTML = animatedHTML;
        }
        
        // For non-contenteditable elements, find and update child elements
        const cellContent = cellElement.querySelector('.cell-content');
        if (cellContent) {
          cellContent.innerHTML = animatedHTML;
        }
        
        // After the animation completes, set the actual text content for editing
        const animationDuration = Math.min(translatedText.split(/\s+/).length * 50 + 1500, 3500); // Cap at 3.5 seconds for long text
        
        // Create a reference to the element to use in the timeout
        const elementForTimeout = cellElement;
        
        setTimeout(() => {
          // Update with plain text after animation finishes
          if (elementForTimeout && elementForTimeout.getAttribute('contenteditable') === 'true') {
            elementForTimeout.textContent = translatedText;
            
            // Save changes to the data model
            handleCellChange(rowKey, columnKey, translatedText);
          }
          
          // Update spans as well
          if (elementForTimeout) {
            const spans = elementForTimeout.querySelectorAll('.cell-content > span:not(.word-animate)');
            spans.forEach(span => {
              span.textContent = translatedText;
            });
            
            // Also update the cell-content's text if there are no spans
            if (spans.length === 0) {
              const cellContent = elementForTimeout.querySelector('.cell-content');
              if (cellContent) {
                cellContent.textContent = translatedText;
                // Add placeholder highlighting after setting the text
                setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
              }
            }
          }
        }, animationDuration);
        
        // Store a reference to the element for the timeout
        const elementRef = cellElement;
        
        // Keep the animation visible for a bit, but not too long
        setTimeout(() => {
          if (elementRef) {
            elementRef.classList.remove('translating');
            
            // Ensure the text is still there after animation
            const contentEditableElement = elementRef.getAttribute('contenteditable') === 'true' ? 
              elementRef : 
              elementRef.querySelector('[contenteditable="true"]');
              
            if (contentEditableElement && !contentEditableElement.textContent) {
              contentEditableElement.textContent = translatedText;
            }
            
            // Check cell-content as well and add placeholder highlighting
            const cellContent = elementRef.querySelector('.cell-content');
            if (cellContent) {
              if (!cellContent.textContent) {
                cellContent.textContent = translatedText;
              }
              // Add placeholder highlighting after animation
              setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
            }
          }
        }, animationDuration + 1000);
        
        // Update the data model and database immediately
        await handleCellChange(rowKey, columnKey, translatedText);
        
        // Force a refresh of the UI to ensure consistency
        setTimeout(() => {
          // Update the translation data to trigger a re-render
          const updatedData = [...translationData];
          
          // Find and update the specific row/column
          const rowIndex = updatedData.findIndex(item => getRowKey(item) === rowKey);
          if (rowIndex !== -1) {
            const stringIndex = updatedData[rowIndex].strings.findIndex(s => s.language_code === columnKey);
            
            // If string exists, update it; otherwise create a new one
            if (stringIndex !== -1) {
              updatedData[rowIndex].strings[stringIndex].value = translatedText;
            } else if (columnKey !== 'key') {
              // Add a new string if it doesn't exist
              updatedData[rowIndex].strings.push({
                id: `temp-${Date.now()}`, // Temporary ID
                key_id: rowKey,
                language_code: columnKey,
                value: translatedText,
                created_at: new Date().toISOString()
              });
            }
          }
          
          setTranslationData(updatedData);
          
          // Add placeholder highlighting after state update
          const cellContent = elementRef?.querySelector('.cell-content');
          if (cellContent) {
            setContentWithHighlightedPlaceholders(cellContent as HTMLElement, translatedText);
          }
        }, animationDuration + 200);
      } else {
        // Even if we can't find the cell, still update the data
        await handleCellChange(rowKey, columnKey, translatedText);
      }
    } else {
      // console.log("TranslationsView: Could not find row data for key:", rowKey);
    }
  };

  // Add a new function to find empty cells in the selected language
  const findEmptyCellsForTranslation = () => {
    if (!selectedLanguage) return [];
    
    const emptyCells = [];
    const allTranslations = Object.values(groupedTranslations).flat();

    // Check each row for empty cells in the selected language
    for (const row of allTranslations) {
      const rowKey = getRowKey(row);
      
      // Get existing translation for this row and language
      const existingTranslation = row.strings.find(str => str.language_code === selectedLanguage);
      
      // If no translation exists or it's empty, add to the list of cells to translate
      if (!existingTranslation || !existingTranslation.value.trim()) {
        // First make sure we have a source text to translate from
        let sourceText = '';
        
        // Try to get source text from the first language
        if (firstLanguage) {
          const sourceString = row.strings.find(str => str.language_code === firstLanguage);
          if (sourceString && sourceString.value.trim()) {
            sourceText = sourceString.value;
          }
        }
        
        // If no source text found, try the key name as fallback
        if (!sourceText && row.key && row.key.key) {
          sourceText = row.key.key;
        }
        
        // Only add cells that have source text to translate from
        if (sourceText) {
          emptyCells.push({
            rowKey,
            columnKey: selectedLanguage,
            sourceText,
            sourceLanguage: firstLanguage || 'key'
          });
        }
      }
    }
    
    return emptyCells;
  };

  // Function to handle the "Translate All Empty" button click
  const handleTranslateAllEmpty = async () => {
    // Find all empty cells that need translation
    const cellsToTranslate = findEmptyCellsForTranslation();
    
    if (cellsToTranslate.length === 0) {
      showNotification("No empty translations found");
      return;
    }
    
    // Show notification about starting the batch translation
    showNotification(`Translating ${cellsToTranslate.length} keys`);
    
    // Set flag to prevent navigation away
    setIsTranslating(true);
    
    // Track progress
    let translatedCount = 0;
    let errorCount = 0;
    
    // Process cells sequentially
    for (const cell of cellsToTranslate) {
      // Check if translation was cancelled
      if (window.sessionStorage.getItem('texta_translation_cancelled')) {
        // console.log("Translation was cancelled, stopping process");
        break;
      }
      
      try {
        // Apply translating class to the current cell
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
        if (cellElement) {
          // Add the translating class
          cellElement.classList.add('translating');
          
          // Add a brief pause to allow the UI to update
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Set the source language
        aiTranslationService.setSourceLanguage(cell.sourceLanguage);
        
        // Call the translation service
        const translatedText = await aiTranslationService.translateText(
          cell.sourceText,
          cell.columnKey
        );
        
        // Check again if translation was cancelled before applying the result
        if (window.sessionStorage.getItem('texta_translation_cancelled')) {
          // console.log("Translation was cancelled before applying result");
          break;
        }
        
        // Update the cell with the translated text
        if (translatedText && translatedText.trim()) {
          // Use the existing handleTableTranslate function to update the cell
          await handleTableTranslate(cell.rowKey, cell.columnKey, translatedText);
          translatedCount++;
          
          // Add a brief pause between translations to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        // console.error(`Error translating cell ${cell.rowKey}-${cell.columnKey}:`, error);
        errorCount++;
        
        // Remove translating class from the failed cell
        const cellElement = document.querySelector(`.editable-table-cell[data-row="${cell.rowKey}"][data-col="${cell.columnKey}"]`);
        if (cellElement) {
          cellElement.classList.remove('translating');
        }
      }
    }
    
    // Complete the batch translation
    setIsTranslating(false);
    
    // Show final notification
    if (errorCount > 0) {
      showNotification(`Translated ${translatedCount} cells with ${errorCount} errors`);
    } else {
      showNotification(`${translatedCount} translated`);
    }
  };

  // Add the handler function for individual cell translation
  const handleCellTranslate = async (rowKey: string, columnKey: string, sourceText: string) => {
    try {
      // Configure the source language in the service if needed
      if (firstLanguage) {
        aiTranslationService.setSourceLanguage(firstLanguage);
      }
      
      // Get the translation
      const translatedText = await aiTranslationService.translateText(
        sourceText,
        columnKey
      );
      
      // Update the UI with the translated text
      if (translatedText) {
        // Use the existing handleTableTranslate to update state and DB
        handleTableTranslate(rowKey, columnKey, translatedText);
        return { result: translatedText };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  };

  // Calculate missing translations for the selected language
  const getMissingTranslationsCount = () => {
    if (!selectedLanguage || !translationData) return 0;
    return translationData.filter(row => {
      const translation = row.strings.find(s => s.language_code === selectedLanguage);
      return !translation || !translation.value.trim();
    }).length;
  };

  const handleCancelTranslation = () => {
    // Set a flag to stop translation
    window.sessionStorage.setItem('texta_translation_cancelled', 'true');
    
    // Reset the translating state
    setIsTranslating(false);
    
    // Remove translating class from all cells
    const cells = document.querySelectorAll('.editable-table-cell');
    cells.forEach(cell => {
      cell.classList.remove('translating');
    });
    
    // Clear the cancelled flag after a short delay
    setTimeout(() => {
      window.sessionStorage.removeItem('texta_translation_cancelled');
    }, 1000);
    
    showNotification('Translation cancelled');
  };

  if (!selectedTeam) {
    return (
      <EmptyStateEnhanced
        title="No team selected"
        subtitle="Please select a team to view projects and translations."
        fullView={true}
      />
    );
  }

  if (selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length === 0) {
    return (
      <EmptyStateEnhanced
        title="No projects in this team"
        subtitle="Create your first project to start managing translation content."
        buttonText="Create Project"
        onButtonClick={() => window.dispatchEvent(new CustomEvent('open-create-project-dialog', { detail: { teamId: selectedTeam.id } }))}
        fullView={true}
      />
    );
  }

  if (selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length > 0 && !selectedProject) {
    return (
      <EmptyStateEnhanced
        title="There are no projects in this team"
        subtitle="Please select a project to view translation keys."
        fullView={true}
      />
    );
  }

  return (
    <div className="translations-view">
      {selectedProject && selectedLanguage && (
        <>
          <ContentCard
            title="Missing Translations"
            subtitle={`${getMissingTranslationsCount()} keys missing translations in ${getLanguageName(selectedLanguage)}`}
            buttonText="Translate All"
            onButtonClick={handleTranslateAllEmpty}
            totalKeys={getMissingTranslationsCount()}
            isComplete={getMissingTranslationsCount() === 0}
            isLoading={isTranslating}
            onCancel={handleCancelTranslation}
          />
          <div className="view-container">
            <div className="content">
              <ProjectLanguageSelector onLanguageSelect={handleLanguageSelect} />

              <div className="translations-section">
                {sortedPrefixes.map(prefix => (
                  <div key={prefix} className="translations-group">
                    <EditableTable
                      columns={getColumnsForPrefix(prefix)}
                      data={groupedTranslations[prefix]}
                      getRowKey={getRowKey}
                      getCellContent={getCellContent}
                      onCellChange={handleCellChange}
                      onDelete={handleEditClick}
                      selectedRows={selectedRows}
                      onRowSelect={handleRowSelect}
                      onCellFocus={handleCellFocus}
                      onCellBlur={handleCellBlur}
                      onTranslate={handleTableTranslate}
                      onTranslateCell={handleCellTranslate}
                      targetLanguage={selectedLanguage}
                      sourceLanguage={firstLanguage}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {!selectedTeam && (
        <EmptyStateEnhanced
          title="No team selected"
          subtitle="Please select a team to view projects and translations."
          fullView={true}
        />
      )}

      {selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length === 0 && (
        <EmptyStateEnhanced
          title="No projects in this team"
          subtitle="Create your first project to start managing translation content."
          buttonText="Create Project"
          onButtonClick={() => window.dispatchEvent(new CustomEvent('open-create-project-dialog', { detail: { teamId: selectedTeam.id } }))}
          fullView={true}
        />
      )}

      {selectedTeam && projects.filter(p => p.team_id === selectedTeam.id).length > 0 && !selectedProject && (
        <EmptyStateEnhanced
          title="There are no projects in this team"
          subtitle="Please select a project to view translation keys."
          fullView={true}
        />
      )}
      
      {error && (
        <EmptyStateEnhanced
          title="Error"
          subtitle={`${error}`}
          fullView={true}
        />
      )}

      {selectedProject && isLoading && (
        <div className="loading-container">
          <Button
            variant="text"
            size="large"
            isLoading={true}
            disabled={true}
          >
          </Button>
        </div>
      )}
      
      {selectedProject && !isLoading && filteredTranslations.length === 0 && (
        <EmptyStateEnhanced
          title="Wonderful, a new beginning"
          subtitle={searchQuery ? `No keys matching "${searchQuery}" found.` : 'Add your first translation key to get started with localization.'}
          buttonText="Add Key"
          onButtonClick={() => setIsKeyDialogOpen(true)}
          fullView={true}
        />
      )}

      <TranslationKeyDialog
        isOpen={isKeyDialogOpen}
        onClose={() => setIsKeyDialogOpen(false)}
        onSave={handleCreateKey}
        isLoading={isCreatingKey}
        firstLanguage={firstLanguage}
      />

      {keyToEdit && (
        <EditKeyDialog
          isOpen={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setKeyToEdit(null);
          }}
          onSave={handleSaveKeyEdit}
          onDelete={handleDeleteKey}
          keyName={keyToEdit.key}
          isLoading={isEditingKey}
        />
      )}

      <ReviewDialog
        isOpen={isReviewDialogOpen}
        onClose={() => setIsReviewDialogOpen(false)}
        onSave={handleSaveReview}
        isLoading={isCreatingReview}
        selectedKeys={selectedKeyNames}
      />

      <SelectReviewDialog
        isOpen={isSelectReviewDialogOpen}
        onClose={() => setIsSelectReviewDialogOpen(false)}
        onSelect={handleReviewSelect}
        projectId={selectedProject?.id || ''}
        isLoading={isAddingToReview}
      />

      {/* Only render ImportTranslationsDialog if selectedProject is defined */}
      {selectedProject && (
        <ImportTranslationsDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          projectId={selectedProject.id}
          onImportComplete={handleImportComplete}
          isLoading={isImporting}
        />
      )}

      {/* Only render ExportTranslationsDialog if selectedProject is defined */}
      {selectedProject && (
        <ExportTranslationsDialog
          isOpen={isExportDialogOpen}
          onClose={() => setIsExportDialogOpen(false)}
          projectId={selectedProject.id}
          projectLanguages={selectedProject.languages || []}
          isLoading={isExporting}
        />
      )}

      <SelectionActionBar
        selectedCount={selectedRows.size}
        onCreateReview={handleCreateReview}
        onAddToReview={handleAddToReview}
        onDeselectAll={handleDeselectAll}
        isVisible={selectedRows.size > 0}
      />
    </div>
  );
};

export default TranslationsView; 